import React, { useCallback, useMemo, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { Divider, Heading, Text, useTheme, VStack } from 'native-base';
import styled from '@emotion/styled';
import {
  USER_PROGRESS_OPTIONS,
  PHASE_OPTIONS,
  USER_TYPES,
  Toolbar,
} from './Toolbar';
import HeaderNav from '../HeaderNav/HeaderNav';
import { Loader } from '../Loader';
import { userPhaseProgressWeightMap } from './utils';
import { HealthCoachDashboardList } from './HealthCoachDashboardList';
import { computeMissedDays } from './HealthCoachDashboardListItem';
import { useUsersList } from '../../api/healthCoach/useUsersList';

const Sticky = styled.div`
  background-color: ${(p) => p.theme.colors.muted['50']};
  position: sticky;
  top: 0;
  z-index: 100;
`;

export const HealthCoachDashboard = () => {
  const {
    colors: { muted },
  } = useTheme();
  const { usersList: users, isLoading: isLoadingUsers } = useUsersList();
  const [filter, setFilter] = useState('');
  const [selectedCoach, setSelectedCoach] = useState(null);
  const [selectedDiagnosis, setSelectedDiagnosis] = useState(null);
  const [selectedPhase, setSelectedPhase] = useState(null);
  const [selectedUserProgress, setSelectedUserProgress] = useState(null);
  const [selectedUserTypes, setSelectedUserTypes] = useState([
    USER_TYPES.freeUser,
    USER_TYPES.trueUser,
  ]);

  const [isFilteringHealthCoach, setIsFilteringHealthCoach] = useState(false);
  const [isFilteringNonHealthCoach, setIsFilteringNonHealthCoach] =
    useState(false);
  const [isFilteringInactiveUsers, setIsFilteringInactiveUsers] =
    useState(false);

  const { coaches, diagnoses } = useMemo(() => {
    if (!users) {
      return { currentCoaches: {}, currentDiagnoses: {} };
    }

    const { currentCoaches, currentDiagnoses } = users.reduce(
      (acc, { diagnosis, coach_first_name, coach_last_name }) => {
        const coachName =
          coach_first_name && coach_last_name
            ? `${coach_first_name} ${coach_last_name}`
            : null;

        if (coachName && !acc.currentCoaches[coachName]) {
          acc.currentCoaches[coachName] = coachName;
        }

        if (diagnosis && !acc.currentDiagnoses[diagnosis]) {
          acc.currentDiagnoses[diagnosis] = diagnosis;
        }
        return acc;
      },
      { currentCoaches: {}, currentDiagnoses: {} }
    );

    const coachOptions = Object.keys(currentCoaches).map((key) => ({
      label: key,
      value: key,
    }));

    const diagnosisOptions = Object.keys(currentDiagnoses).map((key) => ({
      label: key,
      value: key,
    }));

    return { coaches: coachOptions, diagnoses: diagnosisOptions };
  }, [users]);

  const onFilterChange = useCallback((searchTerm) => {
    setFilter(searchTerm);
  }, []);

  const onCoachChange = useCallback((selectedCoachOpt) => {
    setSelectedCoach((selectedCoachOpt && selectedCoachOpt.value) || null);
  }, []);

  const onDiagnosisChange = useCallback((selectedDiagnosisOpt) => {
    setSelectedDiagnosis(
      (selectedDiagnosisOpt && selectedDiagnosisOpt.value) || null
    );
  }, []);

  const onUserProgressChange = useCallback((selectedUserProgress) => {
    setSelectedUserProgress(
      (selectedUserProgress && selectedUserProgress.value) || null
    );
  }, []);

  const onPhaseChange = useCallback((selectedPhaseOpt) => {
    setSelectedPhase((selectedPhaseOpt && selectedPhaseOpt.value) || null);
  }, []);

  const onUserTypesChange = useCallback((userTypes) => {
    setSelectedUserTypes(userTypes.map((uType) => uType.value));
  }, []);

  const onFilterWithHealthCoachChange = useCallback((filterPlan) => {
    setIsFilteringHealthCoach(filterPlan);
  }, []);

  const onFilterWithNonHealthCoachChange = useCallback((filterPlan) => {
    setIsFilteringNonHealthCoach(filterPlan);
  }, []);

  const onFilterWithInactiveUsersChange = useCallback((filterUsers) => {
    setIsFilteringInactiveUsers(filterUsers);
  }, []);

  const filteredUsers = useMemo(() => {
    if (!users) return [];

    const sortedByUserPhaseProgress = users.sort((a, b) => {
      const userPhaseProgressA =
        userPhaseProgressWeightMap[a.user_phase_progress] || 0;
      const userPhaseProgressB =
        userPhaseProgressWeightMap[b.user_phase_progress] || 0;

      return userPhaseProgressB - userPhaseProgressA;
    });

    return sortedByUserPhaseProgress.filter(
      ({
        email,
        id,
        coach_first_name,
        coach_last_name,
        current_phase,
        diagnosis,
        user_phase_progress,
        first_name,
        last_name,
        last_gut_check,
        last_mindgut_activity,
        on_day_in_phase,
        user_type,
      }) => {
        const coachName =
          coach_first_name && coach_last_name
            ? `${coach_first_name} ${coach_last_name}`
            : null;

        const daysSinceLastMindGutActivity = last_mindgut_activity
          ? computeMissedDays(last_mindgut_activity)
          : '';
        const daysSinceLastGutCheck = computeMissedDays(last_gut_check);

        const userName =
          first_name && last_name ? `${first_name} ${last_name}` : null;

        const filterByEmailOrName =
          email.toLowerCase().includes(filter.toLowerCase()) ||
          id.toLowerCase().includes(filter.toLowerCase()) ||
          userName?.toLowerCase().includes(filter.toLowerCase());

        return (
          filterByEmailOrName &&
          (selectedCoach ? coachName?.indexOf(selectedCoach) === 0 : true) &&
          (selectedDiagnosis ? diagnosis === selectedDiagnosis : true) &&
          (selectedUserProgress
            ? userPhaseProgressWeightMap[user_phase_progress] ===
              selectedUserProgress
            : true) &&
          (selectedPhase ? current_phase === selectedPhase : true) &&
          (selectedUserTypes.length
            ? selectedUserTypes.includes(user_type)
            : true) &&
          (isFilteringHealthCoach ? coach_first_name === null : true) &&
          (isFilteringNonHealthCoach ? coach_first_name !== null : true) &&
          (isFilteringInactiveUsers
            ? (typeof daysSinceLastGutCheck == 'number' &&
                daysSinceLastGutCheck <= 30) ||
              (typeof daysSinceLastMindGutActivity == 'number' &&
                daysSinceLastMindGutActivity <= 30) ||
              (!daysSinceLastGutCheck &&
                !daysSinceLastMindGutActivity &&
                on_day_in_phase < 30)
            : true)
        );
      }
    );
  }, [
    filter,
    selectedCoach,
    selectedDiagnosis,
    selectedUserProgress,
    selectedPhase,
    selectedUserTypes,
    users,
    isFilteringHealthCoach,
    isFilteringNonHealthCoach,
    isFilteringInactiveUsers,
  ]);

  return (
    <div
      className="health-coach-dashboard"
      style={{
        height: '100vh',
        backgroundColor: muted['50'],
      }}
    >
      <HeaderNav page={1} changePage={() => null} />

      <div
        style={{
          height: 'calc(100% - 64px)',
          margin: '0 auto',
          paddingLeft: '12px',
          paddingRight: '12px',
        }}
      >
        {isLoadingUsers ? (
          <Loader loading={isLoadingUsers} />
        ) : (
          <VStack p={6} h={'100%'}>
            <Heading color="black" pb={2} textAlign="left" size="xl">
              My Patients ({filteredUsers.length})
            </Heading>
            <Text color="black" fontSize="lg" fontWeight="normal">
              These are your patients and their progress. Click on a patient to
              see their profile & send them a message.
            </Text>

            <Sticky>
              <Divider mt={2} />
              <Toolbar
                checkboxFilters={[
                  {
                    label: 'Exclude users with a health coach',
                    onChange: onFilterWithHealthCoachChange,
                    isChecked: isFilteringHealthCoach,
                  },
                  {
                    label: 'Exclude users without a health coach',
                    onChange: onFilterWithNonHealthCoachChange,
                    isChecked: isFilteringNonHealthCoach,
                  },
                  {
                    label: 'Exclude inactive users',
                    onChange: onFilterWithInactiveUsersChange,
                    isChecked: isFilteringInactiveUsers,
                  },
                ]}
                coaches={coaches}
                diagnoses={diagnoses}
                filterString={filter}
                onCoachChange={onCoachChange}
                onDiagnosisChange={onDiagnosisChange}
                onFilterChange={onFilterChange}
                onUserProgressChange={onUserProgressChange}
                onUserTypesChange={onUserTypesChange}
                onPhaseChange={onPhaseChange}
                selectedCoach={
                  selectedCoach
                    ? { label: selectedCoach, value: selectedCoach }
                    : null
                }
                selectedDiagnosis={
                  selectedDiagnosis
                    ? { label: selectedDiagnosis, value: selectedDiagnosis }
                    : null
                }
                selectedUserProgress={
                  selectedUserProgress
                    ? USER_PROGRESS_OPTIONS.find(
                        (opt) => opt.value === selectedUserProgress
                      )
                    : null
                }
                selectedPhase={
                  selectedPhase
                    ? PHASE_OPTIONS.find((opt) => opt.value === selectedPhase)
                    : null
                }
                selectedUserTypes={selectedUserTypes.map((uType) => ({
                  label: uType,
                  value: uType,
                }))}
              />
              <Divider my={4} />
            </Sticky>

            <HealthCoachDashboardList users={filteredUsers} />
          </VStack>
        )}
      </div>
      <Outlet />
    </div>
  );
};
